import Button from 'tio-ui/components/button';
import Chip from 'tio-ui/components/chip';
import type { ChipSignature } from 'tio-ui/components/chip';
import { getProvider, getStatus } from 'tio-common/helpers/observability';
import { LinkTo } from '@ember/routing';
import Footer from 'tio-ui/components/footer';
import Modal from 'tio-ui/components/modal';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { trackedFunction } from 'reactiveweb/function';
import Component from '@glimmer/component';
import didIntersect from 'ember-scroll-modifiers/modifiers/did-intersect';
import Paginator from 'tio-ui/components/paginator';
import Table from 'tio-ui/components/table';
import type IntlService from 'ember-intl/services/intl';
import type { NotificationsService } from 'tio-ui/components/notifications';
import type { ObservableDocumentExtension as ObservableDocumentModel } from 'tio-data/data/resources/observable-document.ext';
import type { PersonExtension as PersonModel } from 'tio-data/data/resources/person.ext';
import type { Store } from '@warp-drive/core';
import { PhFileArrowUp, PhEnvelope, PhCheckCircle, PhWarning } from 'ember-phosphor-icons';
import { and, or } from 'tio-ui/utilities';
import { discardObservableDocumentRequest } from 'tio-data/requests/observable-document-actions';
import { query } from '@warp-drive/utilities/json-api';
import { restartableTask, timeout } from 'ember-concurrency';
import ResolutionAlert from 'tio-employee/components/slr/resolution-alert';
import {
  memberDocumentStatus,
  NEEDS_ACTION_STATUS,
  toResolutionAlert,
  type ResolutionAlert as ResolutionAlertModel,
} from 'tio-employee/utils/slr/resolution-alert';
import type EmployeeService from 'tio-employee/services/employee';
import type MemberResolutionsService from 'tio-employee/services/member-resolutions';

// Long enough to read the confirmation before the panel folds away.
const CONFIRMATION_COLLAPSE_MS = 900;

const getStatusIntent = (status: string): ChipSignature['Args']['intent'] | undefined => {
  switch (status) {
    case 'PROCESSING':
      return 'primary';
    case 'INVALID':
    case 'FAILED':
    case 'NOT_APPLIED':
      return 'danger';
    case 'PROCESSED':
    case 'APPLIED':
      return 'success';
    case 'IN_DISPUTE':
    case 'PARTIALLY_APPLIED':
    case NEEDS_ACTION_STATUS:
      return 'warning';
  }
};

const getSubmittedIcon = (via: string) => {
  if (via === 'EMAIL') {
    return PhEnvelope;
  }
  return PhFileArrowUp;
};

const getCreatedAt = (document: ObservableDocumentModel) => {
  const createdAtDate = new Date(document.createdAt);
  return createdAtDate.toLocaleString();
};

const PAGE_SIZE = 10;

interface DocumentsTableSignature {
  Args: {
    /** Whose documents to list; the table queries the store for them itself. */
    person: PersonModel;
  };
  Blocks: {
    /** Rendered instead of the table once the query resolves with no documents. */
    empty?: [];
  };
}

/**
 * The person's uploaded documents, newest first and paginated: filename
 * linking to the document page, provider, status, submission channel and
 * date, and a delete action for failed uploads; the filename opens the
 * document page. A document with a pending question from the SLR alert feed
 * reads "Needs Action" and its action toggles the question inline.
 * Rendered by the account-activity page and the beta documents landing so
 * both show one implementation.
 */
export default class DocumentsTable extends Component<DocumentsTableSignature> {
  @service declare store: Store;
  @service declare notifications: NotificationsService;
  @service declare intl: IntlService;
  @service declare employee: EmployeeService;
  @service('member-resolutions') declare memberResolutions: MemberResolutionsService;

  @tracked page = 1;
  @tracked openQuestionDocumentId: string | null = null;
  @tracked limit = PAGE_SIZE;
  @tracked isInViewport = false;
  @tracked showDeleteModal = false;
  @tracked documentToDelete: ObservableDocumentModel | null = null;

  documentsData = trackedFunction(this, async () => {
    const person = this.args.person;

    const { content } = (await this.store.request(
      query<ObservableDocumentModel>('observable-document', {
        'filter[person]': person.id,
        'filter[discarded_at]': 'null',
        include: 'observable-asset',
        sort: '-created-at',
        'page[limit]': PAGE_SIZE,
        'page[offset]': (this.page - 1) * PAGE_SIZE,
      })
    )) as { content: { data: ObservableDocumentModel[]; meta?: { page?: { total?: number } } } };
    return { data: content.data, meta: content.meta };
  });

  get documents() {
    return this.documentsData?.value?.data || [];
  }

  /** True once the first page has loaded and holds nothing. */
  get isEmpty() {
    return this.documentsData.value != null && this.documents.length === 0;
  }

  changePaginatorPage = (page: number) => {
    this.page = page;
  };

  // The feed surfaces one question at a time; it is the only member-readable source of them.
  get questionsByDocument() {
    const questions = new Map<string, ResolutionAlertModel>();
    for (const alert of this.employee.slrAlertsData.value ?? []) {
      const question = toResolutionAlert(alert);
      if (question && !questions.has(question.documentId)) {
        questions.set(question.documentId, question);
      }
    }
    return questions;
  }

  questionFor = (document: ObservableDocumentModel) =>
    document.id ? this.questionsByDocument.get(document.id) : undefined;

  isAnswered = (question: ResolutionAlertModel) => !!this.memberResolutions.answerFor(question);

  statusFor = (document: ObservableDocumentModel) => {
    const question = this.questionFor(document);
    return memberDocumentStatus(document, {
      isOpen: !!question,
      isAnswered: !!question && this.isAnswered(question),
    });
  };

  isQuestionOpen = (document: ObservableDocumentModel) =>
    !!document.id && this.openQuestionDocumentId === document.id;

  toggleQuestion = (document: ObservableDocumentModel) => {
    this.openQuestionDocumentId = this.isQuestionOpen(document) ? null : document.id;
  };

  collapseAfterAnswer = restartableTask(async () => {
    await timeout(CONFIRMATION_COLLAPSE_MS);
    this.openQuestionDocumentId = null;
  });

  deleteDocument = (document: ObservableDocumentModel) => {
    this.documentToDelete = document;
    this.showDeleteModal = true;
  };

  //tbd: convert to shared code in tio-common, clean up tio-admin as well
  confirmDelete = async () => {
    if (!this.documentToDelete || !this.documentToDelete.id) {
      throw new Error('Document ID is missing');
    }

    try {
      await this.store.request(discardObservableDocumentRequest(this.documentToDelete.id));

      const record = this.store.peekRecord(
        'observable-document',
        this.documentToDelete.id
      ) as ObservableDocumentModel | null;
      if (record) {
        this.store.unloadRecord(record);
      }

      this.notifications.add(this.intl.t('observability.documents.index.delete_success'), {
        appearance: 'success',
      });
    } catch (e) {
      this.notifications.add(this.intl.t('observability.documents.index.delete_error'), {
        appearance: 'error',
      });
      console.log('Error occurred while deleting document:', e);
    } finally {
      this.showDeleteModal = false;
      this.documentToDelete = null;
    }
  };

  <template>
    {{#if (and this.isEmpty (has-block "empty"))}}
      {{yield to="empty"}}
    {{else}}
      <Table {{didIntersect onEnter=(fn (mut this.isInViewport) true)}} @isLoading={{false}}>
        <:thead as |options|>
          <options.tr>
            <options.th>{{t "observability.documents.index.headers.filename"}}</options.th>
            <options.th>{{t "observability.documents.index.headers.provider"}}</options.th>
            <options.th>{{t "observability.documents.index.headers.status"}}</options.th>
            <options.th>{{t "observability.documents.index.headers.submitted"}}</options.th>
            <options.th>{{t "observability.documents.index.headers.action"}}</options.th>
          </options.tr>
        </:thead>
        <:tbody as |options|>
          {{#if this.isInViewport}}
            {{#each this.documents as |document|}}
              {{#let (this.questionFor document) as |question|}}
                <options.tr>
                  <options.td>
                    <LinkTo
                      @route="authenticated.observability.documents.show"
                      @model={{document.id}}
                      class="app-text-link app-text-link--inline"
                    >
                      {{document.observableAsset.filename}}
                    </LinkTo>
                  </options.td>
                  <options.td>
                    {{t "observable_document.provider" provider=(getProvider document)}}
                  </options.td>
                  <options.td>
                    <Chip
                      @intent={{getStatusIntent (this.statusFor document)}}
                      data-test-documents-status={{document.id}}
                    >
                      {{t "observable_document.status" state=(this.statusFor document)}}
                    </Chip>
                  </options.td>
                  <options.td>
                    <div class="tio-row-flush flex-row flex-nowrap">

                      {{#let (getSubmittedIcon document.via) as |SubmittedIcon|}}
                        <SubmittedIcon @size="24" class="mr-2" />
                      {{/let}}
                      {{getCreatedAt document}}
                    </div>
                  </options.td>
                  <options.td>
                    {{#if question}}
                      {{#if (this.isAnswered question)}}
                        <span
                          class="tio-row-center h-8 w-8 rounded-lg tio-bg-success-soft"
                          title={{t "observability.documents.index.question_resolved"}}
                          data-test-documents-question-resolved={{document.id}}
                        >
                          <PhCheckCircle @size={{18}} class="tio-fg-success" aria-hidden="true" />
                          <span class="sr-only">
                            {{t "observability.documents.index.question_resolved"}}
                          </span>
                        </span>
                      {{else}}
                        <Button
                          @intent="warning"
                          @appearance="outlined"
                          @size="sm"
                          @icon={{PhWarning}}
                          @ariaLabel={{t "observability.documents.index.question_needs_attention"}}
                          @testHook="documents-table__question-toggle"
                          title={{t "observability.documents.index.question_needs_attention"}}
                          aria-expanded={{if (this.isQuestionOpen document) "true" "false"}}
                          data-test-documents-question-toggle={{document.id}}
                          {{on "click" (fn this.toggleQuestion document)}}
                        />
                      {{/if}}
                    {{else if (eq (getStatus document) "FAILED")}}
                      <div class="tio-flex-tight">
                        <Button
                          @intent="danger"
                          @testHook="documents-table__delete"
                          aria-label={{t "observability.documents.index.discards_document"}}
                          {{on "click" (fn this.deleteDocument document)}}
                        >
                          {{t "observability.documents.index.delete_document"}}
                        </Button>
                      </div>
                    {{/if}}
                  </options.td>
                </options.tr>
                {{#if question}}
                  {{#if (this.isQuestionOpen document)}}
                    <options.tr data-test-documents-question-row={{document.id}}>
                      <options.td colspan="5">
                        <ResolutionAlert
                          @alert={{question}}
                          @surface="documents"
                          @onAnswered={{this.collapseAfterAnswer.perform}}
                        />
                      </options.td>
                    </options.tr>
                  {{/if}}
                {{/if}}
              {{/let}}
            {{/each}}
          {{/if}}
        </:tbody>
        <:tfoot as |options|>
          <options.tr>
            <options.td colspan="4">
              <Paginator
                @totalItems={{or this.documentsData.value.meta.page.total 0}}
                @pageSize={{PAGE_SIZE}}
                @currentPage={{this.page}}
                @onChange={{this.changePaginatorPage}}
              />
            </options.td>
          </options.tr>
        </:tfoot>
      </Table>
    {{/if}}
    <Modal
      @isOpen={{this.showDeleteModal}}
      @onClose={{fn (mut this.showDeleteModal) false}}
      @size="sm"
    >
      <:header>
        {{t "observability.documents.index.delete_confirm_title"}}
      </:header>
      <:body>
        {{t "observability.documents.index.delete_confirm_message"}}
      </:body>
      <:footer>
        <Footer
          @cancelText={{t "common.cancel"}}
          @submitText={{t "common.confirm"}}
          @onCancel={{fn (mut this.showDeleteModal) false}}
          @onSubmit={{this.confirmDelete}}
        />
      </:footer>
    </Modal>
  </template>
}
